// ============================================
// Film Grain Post-Process Effect
// ============================================

import type { IRenderContext } from '../../core/IRenderContext';
import { 
  PostProcessEffect, 
  type PostProcessContext, 
  type PostProcessParameter 
} from '../PostProcessStack';
import { FILM_GRAIN_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Film Grain post-processing effect
 * Adds animated noise to simulate film grain
 */
export class FilmGrainEffect extends PostProcessEffect {
  readonly type = 'filmGrain';
  readonly displayName = 'Film Grain';
  
  // Parameters
  grainIntensity = 0.1;
  response = 0.5;
  
  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = FILM_GRAIN_LAYOUT.createWriter();
  
  getParameters(): PostProcessParameter[] {
    return [
      { name: 'grainIntensity', type: 'float', min: 0, max: 1, default: 0.1, description: 'Grain strength' },
      { name: 'response', type: 'float', min: 0, max: 1, default: 0.5, description: 'Luminance response (higher = less grain in bright areas)' },
    ];
  }
  
  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'grainIntensity': return this.grainIntensity;
      case 'response': return this.response;
      default: return 0;
    }
  }
  
  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'grainIntensity': this.grainIntensity = value; break;
      case 'response': this.response = value; break;
    }
  }
  
  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();
    
    const filmGrainWgsl = getCompiledShader('postprocess/film-grain');
    this.shader = ctx.createShader(filmGrainWgsl, filmGrainWgsl);
    
    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'film-grain-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'film-grain-ldr',
    });
  }
  
  protected onRender(context: PostProcessContext): void {
    const { ctx, source, width, height, time, intensity, isHdrTarget } = context;
    
    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      intensity: this.grainIntensity * intensity,
      response: this.response,
      // Wrapped so the shader's hash input stays in a float-precision-friendly range —
      // with raw seconds the grain visibly freezes/patterns after a few minutes of runtime.
      time: time % 64.0,
      _pad: 0,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }
  
  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
